import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ParticipantPicker } from "@/components/ParticipantPicker";
import { Skeletons, Empty } from "@/components/Primitives";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, Trash2, AlertTriangle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function MeetingEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/meetings/${id}`), api.get("/users")])
      .then(([m, u]) => {
        const d = m.data;
        setUsers(u.data);
        setF({
          title: d.title || "", agenda: d.agenda || "", location: d.location || "",
          division: d.division || "", organization: d.organization || "",
          tags: (d.tags || []).join(", "),
          scheduled_at: toLocalInput(d.scheduled_at),
          duration_minutes: d.duration_minutes || 60,
          language: d.language || "id",
          participant_ids: d.participant_ids || [],
          status: d.status || "scheduled",
        });
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [id]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (uid) => setF((p) => ({
    ...p, participant_ids: p.participant_ids.includes(uid) ? p.participant_ids.filter((x) => x !== uid) : [...p.participant_ids, uid],
  }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error("Judul rapat wajib diisi"); return; }
    setBusy(true);
    try {
      await api.put(`/meetings/${id}`, {
        ...f,
        tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        scheduled_at: new Date(f.scheduled_at).toISOString(),
        duration_minutes: Number(f.duration_minutes) || 60,
      });
      toast.success("Perubahan rapat disimpan");
      nav(`/meetings/${id}`);
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const remove = async () => {
    try { await api.delete(`/meetings/${id}`); toast.success("Rapat dihapus"); nav("/meetings"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!f) return <Skeletons rows={3} className="h-32" />;

  return (
    <div className="max-w-4xl space-y-7" data-testid="meeting-edit-page">
      <div>
        <Button variant="ghost" size="sm" className="mb-3 -ml-2 text-slate-500" onClick={() => nav(`/meetings/${id}`)} data-testid="back-btn">
          <ArrowLeft size={15} className="mr-1.5" /> Kembali ke detail
        </Button>
        <p className="label-xs">Ubah Rapat</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Edit Meeting</h1>
      </div>

      <form onSubmit={save} className="space-y-6" data-testid="meeting-edit-form">
        <div className="clay p-7 space-y-6">
          <p className="label-xs">Informasi Rapat</p>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Judul Rapat *</Label>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} className="h-11 rounded-xl" data-testid="edit-title-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Agenda</Label>
            <Textarea value={f.agenda} onChange={(e) => set("agenda", e.target.value)} rows={4} className="rounded-xl" data-testid="edit-agenda-input" />
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Jadwal</Label>
              <Input type="datetime-local" value={f.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} className="h-11 rounded-xl" data-testid="edit-schedule-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Durasi (menit)</Label>
              <Input type="number" min={5} value={f.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="h-11 rounded-xl" data-testid="edit-duration-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Bahasa</Label>
              <Select value={f.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="edit-language-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="id">Bahasa Indonesia</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ja">日本語</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="clay p-7 space-y-6">
          <p className="label-xs">Metadata</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Lokasi</Label>
              <Input value={f.location} onChange={(e) => set("location", e.target.value)} className="h-11 rounded-xl" data-testid="edit-location-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Divisi</Label>
              <Input value={f.division} onChange={(e) => set("division", e.target.value)} className="h-11 rounded-xl" data-testid="edit-division-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Organisasi</Label>
              <Input value={f.organization} onChange={(e) => set("organization", e.target.value)} className="h-11 rounded-xl" data-testid="edit-organization-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Tags (pisahkan koma)</Label>
              <Input value={f.tags} onChange={(e) => set("tags", e.target.value)} className="h-11 rounded-xl" data-testid="edit-tags-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Status</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Terjadwal</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="processing">Diproses</SelectItem>
                  <SelectItem value="completed">Selesai</SelectItem>
                  <SelectItem value="cancelled">Dibatalkan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="clay p-7 space-y-5">
          <div>
            <p className="label-xs">Peserta</p>
            <p className="mt-1.5 text-sm text-slate-500">{f.participant_ids.length} peserta dipilih. Peserta dengan voiceprint aktif dikenali otomatis.</p>
          </div>
          {users.length === 0
            ? <Empty icon={AlertTriangle} title="Belum ada pengguna" sub="Tambahkan pengguna pada menu User & Role." />
            : <ParticipantPicker users={users} selected={f.participant_ids} onToggle={toggle} testId="edit-participant-list" />}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="outline" className="h-11 rounded-xl border-red-200 text-red-600 hover:bg-red-50" data-testid="edit-delete-btn">
                <Trash2 size={15} className="mr-2" /> Hapus Rapat
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="edit-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Hapus rapat ini?</AlertDialogTitle>
                <AlertDialogDescription>Transkrip, ringkasan AI, action items, dan riwayat chat akan dihapus permanen.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="edit-cancel-delete-btn">Batal</AlertDialogCancel>
                <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="edit-confirm-delete-btn">Hapus</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <div className="flex gap-3">
            <Button type="button" variant="ghost" className="h-11 rounded-xl text-slate-600" onClick={() => nav(`/meetings/${id}`)} data-testid="edit-cancel-btn">Batal</Button>
            <Button type="submit" className="h-11 rounded-xl grad-brand text-white px-6" disabled={busy} data-testid="edit-save-btn">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Simpan Perubahan
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
